import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { Suspense, useCallback, useMemo, useState } from 'react'
import { SPACING } from '@common/theme/spacing'
import {
  client,
  downloadExamStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdExamDownloadGet,
} from '@common/config/api/client'
import { useNonDicomFileUrl } from './hooks/useNonDicomFileUrl'
import { findViewer } from './registry'
import DownloadOnlyViewer from './viewers/DownloadOnlyViewer'

export type NonDicomViewerHostProps = {
  fileName?: string | null
  fileExtension?: string | null
  examMetadataId?: string | null
  studyId: string
  subjectId: string
  timepointSubmissionId: string
  containerSubmissionId: string
}

const PREVIEW_BOX_BASE_SX = {
  position: 'relative' as const,
  width: '100%',
  height: '70vh',
  minHeight: 400,
  maxHeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}

const OVERLAY_SX = {
  position: 'absolute' as const,
  inset: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  zIndex: 10,
}

export default function NonDicomViewerHost({
  fileName,
  fileExtension,
  examMetadataId,
  studyId,
  subjectId,
  timepointSubmissionId,
  containerSubmissionId,
}: NonDicomViewerHostProps) {
  const viewer = useMemo(() => findViewer(fileExtension), [fileExtension])
  const [isContentLoading, setIsContentLoading] = useState(true)
  const [contentError, setContentError] = useState<Error | null>(null)

  // Video needs a black letterbox; images and PDFs should sit on a neutral background
  // so the dark box does not look like a loading failure before the content arrives.
  const previewBoxSx = useMemo(
    () => ({ ...PREVIEW_BOX_BASE_SX, bgcolor: viewer?.id === 'video' ? '#000' : 'transparent' }),
    [viewer],
  )

  const params = useMemo(
    () =>
      examMetadataId ? { studyId, subjectId, timepointSubmissionId, containerSubmissionId, examMetadataId } : undefined,
    [studyId, subjectId, timepointSubmissionId, containerSubmissionId, examMetadataId],
  )

  // Only auto-fetch when something can actually render the file; download-only types fetch on click
  const { data, isFetching, isError, refetch } = useNonDicomFileUrl(params, !!viewer)

  const handleReady = useCallback(() => setIsContentLoading(false), [])
  const handleError = useCallback((error: Error) => setContentError(error), [])

  const handleRetry = useCallback(() => {
    setIsContentLoading(true)
    setContentError(null)
    void refetch()
  }, [refetch])

  const [isDownloading, setIsDownloading] = useState(false)
  const handleDownload = useCallback(async () => {
    setIsDownloading(true)
    try {
      // Use the same download endpoint as the kebab-menu download button — it handles all
      // file types (including waveform/signal formats) and is proven to work in production.
      // The non-DICOM preview endpoint (/studies/site/non-dicom-file) is intentionally NOT
      // used here: it requires fully-processed file metadata and would return 404 for file
      // types that are not yet supported for preview.
      const response =
        await downloadExamStudiesStudyIdSubjectsSubjectIdTimepointSubmissionTimepointSubmissionIdExamDownloadGet({
          client,
          path: {
            study_id: studyId,
            subject_id: subjectId,
            timepoint_submission_id: timepointSubmissionId,
          },
          query: { container_submission_id: containerSubmissionId },
          throwOnError: true,
        })

      if (response.data?.url) {
        const fileResponse = await fetch(response.data.url)
        const blob = await fileResponse.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        // Prefer the prop filename; fall back to extracting it from the signed URL
        link.download = fileName || response.data.url.split('/').pop()?.split('?')[0] || 'download'
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(blobUrl)
        document.body.removeChild(link)
      }
    } catch (error) {
      setContentError(error instanceof Error ? error : new Error('Download failed. Please try again.'))
    } finally {
      setIsDownloading(false)
    }
  }, [studyId, subjectId, timepointSubmissionId, containerSubmissionId, fileName])

  const signedUrl = data?.url

  if (!examMetadataId) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <Typography color="text.secondary">File is not available for preview yet.</Typography>
      </Stack>
    )
  }

  if (!viewer) {
    return (
      <DownloadOnlyViewer
        fileName={fileName}
        fileExtension={fileExtension}
        isDownloading={isDownloading}
        onDownload={() => void handleDownload()}
      />
    )
  }

  if (isError || contentError) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" spacing={SPACING.spacingLg}>
        <Typography color="error">{contentError?.message ?? 'Failed to load preview.'}</Typography>
        <Stack direction="row" spacing={SPACING.spacingLg}>
          <Button variant="contained" onClick={handleRetry}>
            Retry
          </Button>
          {signedUrl && (
            <Button variant="outlined" onClick={() => window.open(signedUrl, '_blank', 'noopener,noreferrer')}>
              Open in new tab
            </Button>
          )}
        </Stack>
      </Stack>
    )
  }

  if (viewer.transport !== 'url') {
    // Byte-transport viewers (waveform formats) need the host to fetch and hand over buffers,
    // including sibling companion files. Not built yet, so fail visibly rather than render nothing.
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <Typography color="text.secondary">This file type is not supported for preview yet.</Typography>
      </Stack>
    )
  }

  const ViewerComponent = viewer.component
  const showOverlay = isFetching || !signedUrl || isContentLoading

  return (
    <Box sx={previewBoxSx}>
      {signedUrl && (
        <Suspense fallback={null}>
          <ViewerComponent
            key={signedUrl}
            source={{ kind: 'url', url: signedUrl }}
            mimeType={data?.mime_type ?? ''}
            fileName={data?.file_name ?? fileName ?? ''}
            onReady={handleReady}
            onError={handleError}
          />
        </Suspense>
      )}
      {showOverlay && (
        <Box sx={OVERLAY_SX}>
          <CircularProgress color="inherit" />
          <Typography variant="body2" mt={SPACING.spacingLg}>
            {signedUrl ? 'Loading preview...' : 'Fetching preview link...'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
